import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { underscored } from 'underscore.string';
import classnames from 'classnames';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@cargomatic/ui';
import SessionStore from 'stores/SessionStore';
import { isNumber } from 'lodash';
import Alert from '../../ui/Alert';
import ErrorAlert from '../../ui/ErrorAlert';

import {
  createAccessorial,
  updateAccessorial,
} from '../../../utils/AccessorialWebAPIUtils';

const ICON_COUNT = 36;

const shipmentTypes = [
  { localizationSlug: 'DRAYAGE', value: 'drayage' },
  { localizationSlug: 'SHORTHAUL', value: 'shorthaul' },
];

const rateTypes = [
  { localizationSlug: 'FLAT', value: 'flat' },
  { localizationSlug: 'HOURLY', value: 'hourly' },
  { localizationSlug: 'DAILY', value: 'daily' },
];

const unitTypes = [
  { localizationSlug: 'DECIMAL', value: 'decimal' },
  { localizationSlug: 'NUMBER', value: 'number' },
];

const ORDER_CREATION_DATE = 'ORDER_CREATION_DATE';
const PICKUP_STOP = 'PICKUP_STOP';
const DELIVERY_STOP = 'DELIVERY_STOP';
const DELIVERY_EMPTY = 'DELIVERY_EMPTY';

export const anchorEventOptions = [
  {
    localizationSlug: ORDER_CREATION_DATE,
    name: 'Order Creation Date',
    value: ORDER_CREATION_DATE,
  },
  {
    localizationSlug: PICKUP_STOP,
    name: 'Pickup Stop',
    value: PICKUP_STOP,
  },
  {
    localizationSlug: DELIVERY_STOP,
    name: 'Delivery Stop',
    value: DELIVERY_STOP,
  },
  {
    localizationSlug: DELIVERY_EMPTY,
    name: 'Delivery Empty',
    value: DELIVERY_EMPTY,
  },
];

function mapAccessorial(accessorial = {}) {
  return {
    name: '',
    shipment_type: 'drayage',
    begin_date_required: false,
    end_date_required: false,
    location_required: false,
    description_required: false,
    rate_type: 'flat',
    unit_type: 'decimal',
    admin_only: false,
    ...accessorial,
  };
}

class AccessorialForm extends Component {
  constructor(props) {
    const { accessorial } = props;
    super(props);
    this.state = {
      accessorial: mapAccessorial(accessorial),
      success: false,
      saving: false,
      errors: {},
    };
  }

  handleSelectIcon = (icon_slug) => () => {
    this.setState((prevState) => ({
      accessorial: { ...prevState.accessorial, icon_slug },
    }));
  };

  handleFieldChange = (field, type) => (value) => {
    let changedValue = value;
    if (type === 'number' && isNumber(value)) {
      changedValue = Number(value);
    }
    this.setState((prevState) => ({
      accessorial: { ...prevState.accessorial, [field]: changedValue },
    }));
  };

  handleInputChange = (field, type) => (e) => {
    this.handleFieldChange(
      field,
      type,
    )(
      type === 'number' && e.target.value !== ''
        ? Number(e.target.value)
        : e.target.value,
    );
  };

  handleSwitchChange = (field) => (checked) => {
    this.handleFieldChange(field)(checked);
  };

  handleSelectChange = (field) => (value) => {
    this.handleFieldChange(field)(value);
  };

  handleSave = () => {
    const { t } = this.props;
    this.setState(
      (prevState) => {
        const errors = this.validate(prevState.accessorial);
        const saving = Object.keys(errors).length === 0;
        return { errors, saving };
      },
      async () => {
        if (!this.state.saving) return;

        const saveFn = this.state.accessorial._id
          ? updateAccessorial
          : createAccessorial;

        try {
          const result = await saveFn({
            ...this.state.accessorial,
            slug: underscored(this.state.accessorial.name.toLowerCase()),
          });

          const {
            entity: { accessorial },
          } = result;

          this.setState(
            { saving: false, errors: {}, accessorial, success: true },
            () => {
              setTimeout(() => {
                this.props.onSaveSuccess(this.state.accessorial);
              }, 400);
            },
          );
        } catch (e) {
          console.error(e);
          let error;
          if (e.entity && e.entity.error.includes('duplicate key error')) {
            if (e.entity.error.includes('charge_code')) {
              error = t('CHARGE_CODE_ALREADY_EXISTS');
            } else {
              error = t('ACCESSORIAL_ALREADY_EXISTS');
            }
          } else {
            error = t('PROBLEM_SAVING_ACCESSORIAL');
          }

          this.setState({
            saving: false,
            success: false,
            errors: { base: error },
          });
        }
      },
    );
  };

  validate(accessorial) {
    const { t } = this.props;
    const { name, charge_code, rate_type, unit_type } = accessorial;

    const errors = {};
    if (!name) errors.name = t('NAME_REQUIRED_ERROR');
    if (!charge_code) errors.charge_code = t('CHARGE_CODE_REQUIRED_ERROR');
    if (!rate_type) errors.rate_type = t('RATE_TYPE_REQUIRED_ERROR');
    if (!unit_type) errors.unit_type = t('UNIT_TYPE_REQUIRED_ERROR');
    return errors;
  }

  renderSuccess() {
    const { t } = this.props;
    const { success, saving } = this.state;
    if (saving || !success) return null;

    return (
      <Alert
        type="success"
        onClose={this.handleCloseSuccessAlert}
        canClose
        title={t('ACCESSORIAL_SAVED_SUCCESSFULLY')}
      />
    );
  }

  renderErrors() {
    const { t } = this.props;
    const { errors } = this.state;
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return null;

    return (
      <ErrorAlert
        title={t('FIX_FOLLOWING_ISSUES')}
        canClose
        onClose={this.handleHideErrorAlert}
      >
        <ul className="ml-0 list-reset">
          {errorKeys.map((errorKey) => (
            <li key={errorKey}>{this.state.errors[errorKey]}</li>
          ))}
        </ul>
      </ErrorAlert>
    );
  }

  renderSwitchRow(id, field, labelKey) {
    const { t } = this.props;
    const checked = this.state.accessorial[field];
    return (
      <div className="flex items-center gap-2">
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={this.handleSwitchChange(field)}
        />
        <Label htmlFor={id} className="text-sm font-normal cursor-pointer">
          {t(labelKey)}
        </Label>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const {
      accessorial: {
        name,
        charge_code,
        shipment_type,
        rate_type,
        unit_type,
        days_before_auto_decline,
        anchor_event,
      },
    } = this.state;
    const isDrayage = shipment_type === 'drayage';
    const hasFinanceAdminRole = SessionStore.hasFinanceAdminRole();

    return (
      <div className="space-y-4">
        {this.renderSuccess()}
        {this.renderErrors()}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="accessorial-name">{t('NAME')}</Label>
            <Input
              id="accessorial-name"
              type="text"
              value={name || ''}
              placeholder={t('ENTER_ACCESSORIAL_NAME')}
              onChange={this.handleInputChange('name')}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="accessorial-charge-code">{t('CHARGE_CODE')}</Label>
            <Input
              id="accessorial-charge-code"
              type="text"
              value={charge_code || ''}
              placeholder={t('ENTER_ACCESSORIAL_CHARGE_CODE')}
              onChange={this.handleInputChange('charge_code')}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label htmlFor="shipment-type">{t('SHIPMENT_TYPE')}</Label>
            <Select
              value={shipment_type}
              onValueChange={this.handleSelectChange('shipment_type')}
            >
              <SelectTrigger id="shipment-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {shipmentTypes.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {t(item.localizationSlug)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="unit-type">{t('UNIT_TYPE')}</Label>
            <Select
              value={unit_type}
              onValueChange={this.handleSelectChange('unit_type')}
            >
              <SelectTrigger id="unit-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {unitTypes.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {t(item.localizationSlug)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="rate-type">{t('RATE_TYPE')}</Label>
            <Select
              value={rate_type}
              onValueChange={this.handleSelectChange('rate_type')}
            >
              <SelectTrigger id="rate-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rateTypes.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {t(item.localizationSlug)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            {this.renderSwitchRow(
              'admin_only',
              'admin_only',
              'ADMIN_ONLY_SELECT',
            )}

            <div>
              <Label className="block mb-2">{t('REQUIRED_FIELDS')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {this.renderSwitchRow(
                  'begin_date_required',
                  'begin_date_required',
                  'BEGIN_DATE',
                )}
                {this.renderSwitchRow(
                  'end_date_required',
                  'end_date_required',
                  'END_DATE',
                )}
                {this.renderSwitchRow(
                  'location_required',
                  'location_required',
                  'LOCATION',
                )}
                {this.renderSwitchRow(
                  'description_required',
                  'description_required',
                  'DESCRIPTION',
                )}
              </div>
            </div>
          </div>

          {isDrayage && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="accessorial-request-limit">
                  {t('ACCESSORIAL_REQUEST_LIMIT')} ({t('BUSINESS_DAYS')})
                </Label>
                <Input
                  id="accessorial-request-limit"
                  type="number"
                  min="0"
                  value={days_before_auto_decline ?? ''}
                  onChange={this.handleInputChange(
                    'days_before_auto_decline',
                    'number',
                  )}
                  disabled={!hasFinanceAdminRole}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="anchor-event">{t('ANCHOR_DATE')}</Label>
                <Select
                  value={anchor_event ?? DELIVERY_STOP}
                  onValueChange={this.handleSelectChange('anchor_event')}
                  disabled={!hasFinanceAdminRole}
                >
                  <SelectTrigger id="anchor-event">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {anchorEventOptions.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {t(item.localizationSlug)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <div>
          <Label className="block mb-2">{t('ICON')}</Label>
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
            {Array(ICON_COUNT)
              .fill()
              .map((_, i) => {
                const iconSlug = `icon-${i + 1}`;
                const active =
                  this.state.accessorial.icon_slug === `icon-${i + 1}`;
                const cellClassName = classnames(
                  'flex items-center justify-center w-full aspect-square bg-white rounded border cursor-pointer hover:border-grey',
                  {
                    'border-green border-2': active,
                    'border-grey-lighter': !active,
                  },
                );
                const iconClassName = classnames(
                  iconSlug,
                  'icon-normal',
                  'mx-0',
                );
                return (
                  <button
                    type="button"
                    key={i}
                    className={cellClassName}
                    onClick={this.handleSelectIcon(iconSlug)}
                  >
                    <i className={iconClassName} />
                  </button>
                );
              })}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            disabled={this.state.saving}
            onClick={this.props.onCancel}
          >
            {t('CANCEL')}
          </Button>
          <Button
            type="button"
            onClick={this.handleSave}
            disabled={this.state.saving}
          >
            {t('SAVE')}
          </Button>
        </div>
      </div>
    );
  }
}

AccessorialForm.propTypes = {
  accessorial: PropTypes.shape({
    name: PropTypes.string,
    shipment_type: PropTypes.oneOf(['drayage', 'shorthaul']),
    begin_date_required: PropTypes.bool,
    end_date_required: PropTypes.bool,
    location_required: PropTypes.bool,
    description_required: PropTypes.bool,
    rate_type: PropTypes.oneOf(['flat', 'hourly', 'daily']),
    unit_type: PropTypes.oneOf(['decimal', 'integer']),
  }),
  onCancel: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
};

export default withTranslation()(AccessorialForm);
